/**
 * useAnalysis — handler functions for file selection and analysis dispatch.
 * Extracts handleFileSelected, handleAnalyseWithPrompt, handleAnalyseInternal,
 * handleConversionConfirm, handleConversionCancel, handleCancel, and resetToIdle
 * from PrimateVision.tsx.
 *
 * All state lives in the caller; this hook receives setters and refs as params
 * and returns stable handler functions (no internal state).
 */
import { type MutableRefObject, type Dispatch, type SetStateAction } from 'react';
import { isSupported } from '../lib/video-converter';
import {
  updateUserVideo,
  saveUserVideoResult,
  type PrimateVisionResult,
} from '../lib/api';
// PRI-496 Lane B: analysis dispatch/poll/cancel + video upload ride the
// PUBLIC contract (POST /v1/videos → S3 PUT → complete; POST /v1/analyses;
// GET /v1/analyses/{id}) — the same surface an API customer uses.
import {
  uploadPublicVideo,
  createPublicAnalysis,
  createAnalysisMultipart,
  fitsOneShot,
  getVisionResult,
  cancelVisionJob,
} from '../lib/api/public-inference';
import { COMPILED_QUERY_DELIMITER } from '../components/v2/pv2-shared';
import {
  trackVideoUploaded,
  trackVideoAnalysed,
  trackAnalysisCancelled,
  trackUserVideoSaved,
} from '../telemetry/events';
import { extractThumbnail } from './useFileUpload';
import { type PageState, type VideoEntry, type DemoVideoLibraryEntry } from './types';
import { type UserVideo } from '../lib/api';
import type { CompiledQuery } from '../components/v2/compiled-query-types';

const MAX_UPLOAD_BYTES = parseInt(import.meta.env.VITE_MAX_UPLOAD_BYTES ?? '524288000', 10);
const MAX_UPLOAD_MB = Math.round(MAX_UPLOAD_BYTES / 1024 / 1024);

export interface UseAnalysisParams {
  // Auth
  isSignedIn: boolean;
  isPro: boolean;
  getToken: ((options?: { template?: string }) => Promise<string | null>) | null;

  // File upload state
  setSelectedFile: (f: File | null) => void;
  setSelectedDemoVideo: (id: string | null) => void;
  setConversionFile: (f: File | null) => void;
  setShowConversionDialog: (v: boolean) => void;
  setFileError: (msg: string | null) => void;
  setUploadProgress: (pct: number | ((prev: number) => number)) => void;
  setUploadPath: (path: 'checking' | 'uploading' | null) => void;

  // Video library state
  videoQueue: VideoEntry[];
  setVideoQueue: Dispatch<SetStateAction<VideoEntry[]>>;
  activeVideoId: string | null;
  setActiveVideoId: (id: string | null) => void;
  setSavedUserVideos: Dispatch<SetStateAction<UserVideo[]>>;
  setSelectedSavedVideo: (v: UserVideo | null) => void;
  selectedFile: File | null;
  selectedDemoVideo: string | null;
  selectedSavedVideo: UserVideo | null;
  demoVideos: DemoVideoLibraryEntry[];
  demoUsageCount: number;
  setDemoUsageCount: (n: number) => void;
  DEMO_LIMIT: number;

  // Inference state
  pageStateRef: MutableRefObject<PageState>;
  setPageStateSync: (s: PageState) => void;
  setJobId: (id: string | null) => void;
  jobId: string | null;
  setResult: (r: PrimateVisionResult | null) => void;
  setDisplayedResult: (r: PrimateVisionResult | null) => void;
  setDisplayedJobId: (id: string | null) => void;
  setAnalysisErrorMessage: (msg: string | null) => void;
  setQueuePosition: (pos: number | null) => void;
  setJobProgress: (p: { stage: string; pct: number; message: string } | null) => void;
  setRevealPhase: (p: 'hidden' | 'answer' | 'result') => void;
  setIsDemoResult: (v: boolean) => void;
  setProcessingStep: (s: number) => void;
  resultCacheRef: MutableRefObject<Map<string, { jobId: string; result: PrimateVisionResult }>>;
  analysisGenRef: MutableRefObject<number>;
  videoKeyRef: MutableRefObject<string | null>;
  startPolling: (id: string, prompt: string, generation?: number) => void;
  stopPolling: () => void;

  // Video queue ref (for polling S3 upload completion)
  videoQueueRef: MutableRefObject<VideoEntry[]>;

  // Prompts list (for analytics: is the prompt custom?)
  prompts: string[];

  // Prompt state
  setSelectedPrompt: (p: string) => void;

  // UI state
  setResultTab: (tab: 'result' | 'api' | 'benchmarks' | 'compare') => void;

  // SAM settings (PRI-437 P8: quality selection removed — one metered rate)
  useSam?: boolean;
}

export interface UseAnalysisReturn {
  handleFileSelected: (file: File) => Promise<void>;
  handleConversionConfirm: (convertedFile: File) => Promise<void>;
  handleConversionCancel: () => void;
  handleAnalyseWithPrompt: (prompt: string, compiledQuery?: CompiledQuery | null) => Promise<void>;
  handleCancel: () => Promise<void>;
  resetToIdle: () => void;
}

/**
 * PRI-496 Lane B: decode the |||COMPILED_QUERY:<base64>||| wire envelope the
 * composer embeds into prompts. The legacy inference engine extracted it
 * server-side; the public plane takes prompt XOR query — so the client
 * decodes the envelope and sends the structured query explicitly
 * (parse_mode: client), keeping lane-editor overrides intact.
 */
export function decodeWirePrompt(wirePrompt: string): { text: string; query: Record<string, unknown> | null } {
  const idx = wirePrompt.indexOf(COMPILED_QUERY_DELIMITER);
  if (idx === -1) return { text: wirePrompt, query: null };
  const text = wirePrompt.slice(0, idx).trim();
  try {
    const b64 = wirePrompt.slice(idx + COMPILED_QUERY_DELIMITER.length).replace(/\|\|\|\s*$/, '');
    const json = (typeof atob === 'function')
      ? decodeURIComponent(escape(atob(b64)))
      : Buffer.from(b64, 'base64').toString('utf-8');
    return { text, query: JSON.parse(json) as Record<string, unknown> };
  } catch {
    // Malformed envelope — send the clean text and let the server compile.
    return { text, query: null };
  }
}

export function useAnalysis(p: UseAnalysisParams): UseAnalysisReturn {

  async function handleFileSelected(file: File): Promise<void> {
    console.log('[PV] handleFileSelected called', file.name, file.type, file.size);
    try {
      const ext = ('.' + (file.name.split('.').pop() ?? '')).toLowerCase();
      const isMP4 = ext === '.mp4' || file.type === 'video/mp4';
      console.log('[PV] ext:', ext, 'isMP4:', isMP4, 'type:', file.type);

      if (!isSupported(file)) {
        console.log('[PV] unsupported format');
        p.setFileError("This format isn't supported. Try MP4 or MOV.");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        console.log('[PV] large file:', file.size);
        p.setFileError(`This file is ${(file.size / 1024 / 1024).toFixed(1)}MB — max is ${MAX_UPLOAD_MB}MB.`);
        return;
      }
      p.setFileError(null);

      const entryId = crypto.randomUUID();
      const entry: VideoEntry = { id: entryId, file, thumbnailUrl: '' };
      p.setVideoQueue(prev => [...prev, entry]);
      p.setActiveVideoId(entry.id);
      p.setSelectedFile(file);
      p.setSelectedDemoVideo(null);
      p.setSelectedSavedVideo(null);
      p.setDisplayedJobId(null);
      p.setDisplayedResult(null);
      p.setResult(null);
      p.setSelectedPrompt('');
      p.setPageStateSync('idle');
      p.setAnalysisErrorMessage(null);
      p.videoKeyRef.current = null;
      p.resultCacheRef.current.clear();
      p.analysisGenRef.current = 0;

      const pendingEntry = { userVideoId: null as string | null, thumbnailDataUrl: null as string | null };

      console.log('[PV] extracting thumbnail...');
      extractThumbnail(file).then(thumb => {
        console.log('[PV] thumbnail ready:', thumb.length);
        p.setVideoQueue(prev => prev.map(v => v.id === entryId ? { ...v, thumbnailUrl: thumb } : v));
        pendingEntry.thumbnailDataUrl = thumb;
        if (p.isSignedIn && pendingEntry.userVideoId) {
          void updateUserVideo(pendingEntry.userVideoId, { thumbnail_data_url: thumb }).catch(() => {});
        }
        p.setSavedUserVideos(prev => prev.map(v => v.id === entryId ? { ...v, thumbnail_data_url: thumb } : v));
      }).catch(() => { /* non-fatal */ });

      // PRI-496 Lane B: ONE public upload replaces the legacy dual-write
      // (presign + createUserVideo). POST /v1/videos creates the library row
      // server-side (public_id IS the row identity we keep client-side); the
      // internal PATCH persists console-only fields (display name, thumbnail)
      // onto the same row — the internal routes resolve public ids.
      // Signed-in only: anon free-grant keys must not be provisioned/burned on
      // file-select; guests upload at submit time (see handleAnalyseInternal).
      if (p.isSignedIn) {
        void uploadPublicVideo(file).then(video => {
          p.setVideoQueue(prev => prev.map(v =>
            v.id === entryId ? { ...v, userVideoId: video.id, publicVideoId: video.id } : v
          ));
          pendingEntry.userVideoId = video.id;
          console.log('[PV] Source uploaded (public videos plane):', video.id);
          void updateUserVideo(video.id, {
            display_name: file.name.replace(/\.[^.]+$/, ''),
            ...(pendingEntry.thumbnailDataUrl ? { thumbnail_data_url: pendingEntry.thumbnailDataUrl } : {}),
          }).catch(() => { /* non-fatal — console cosmetics only */ });
          // Prepend to the sidebar list immediately (no page refresh needed).
          const libraryRow: UserVideo = {
            id: video.id,
            display_name: file.name.replace(/\.[^.]+$/, ''),
            filename: file.name,
            file_size_bytes: file.size,
            duration_seconds: video.duration_s ?? null,
            scan_id: null,
            source_s3_key: null,
            thumbnail_data_url: pendingEntry.thumbnailDataUrl,
            sort_order: 0,
            created_at: video.created_at ?? new Date().toISOString(),
            public_id: video.id,
            status: video.status,
            results_by_prompt: {},
          };
          p.setSavedUserVideos(prev => {
            if (prev.some(v => v.id === libraryRow.id)) {
              return prev.map(v => v.id === libraryRow.id ? { ...v, thumbnail_data_url: pendingEntry.thumbnailDataUrl } : v);
            }
            return [libraryRow, ...prev];
          });
          // Set as selectedSavedVideo so V2 session save can find the userVideoId
          // when displayedJobId fires after inference completes.
          p.setSelectedSavedVideo(libraryRow);
          trackUserVideoSaved({ filename: file.name, file_size_bytes: file.size, user_plan: p.isPro ? 'pro' : 'free' });
        }).catch(err => {
          console.warn('[PV] Public video upload failed (non-fatal — submit re-uploads):', err);
        });
      }
    } catch (err) {
      console.error('[handleFileSelected] Error:', err);
      const ext = ('.' + (file.name.split('.').pop() ?? '')).toLowerCase();
      if (ext !== '.mp4') {
        p.setFileError('Failed to read this file. Please try a different video.');
      } else {
        p.setFileError('Failed to read this file. Please try another video.');
      }
    }
  }

  async function handleConversionConfirm(convertedFile: File): Promise<void> {
    p.setShowConversionDialog(false);
    p.setConversionFile(null);
    const thumb = await extractThumbnail(convertedFile);
    const entry: VideoEntry = { id: crypto.randomUUID(), file: convertedFile, thumbnailUrl: thumb };
    p.setVideoQueue(prev => [...prev, entry]);
    p.setActiveVideoId(entry.id);
    p.setSelectedFile(convertedFile);
  }

  function handleConversionCancel(): void {
    p.setShowConversionDialog(false);
    p.setConversionFile(null);
  }

  async function handleAnalyseInternal(prompt: string, generation?: number): Promise<void> {
    const isStale = () => generation !== undefined && p.analysisGenRef.current !== generation;
    if (!p.selectedFile || !prompt) return;
    p.setResult(null);
    p.setDisplayedResult(null);
    p.setDisplayedJobId(null);
    p.videoKeyRef.current = null;
    p.setIsDemoResult(false);
    // Set uploadPath before pageState so the fake-progress timer in useInference
    // doesn't fire for real file uploads (it checks uploadPath !== 'uploading').
    p.setUploadPath('uploading');
    p.setPageStateSync('uploading');
    try {
      {
        const activeEntry = p.videoQueue.find(v => v.id === p.activeVideoId);
        let publicVideoId = activeEntry?.publicVideoId ?? null;

        // If the background public upload is still in flight, poll
        // videoQueueRef for up to 10s (upload sets publicVideoId when done).
        if (!publicVideoId && p.isSignedIn) {
          const POLL_INTERVAL_MS = 100;
          const MAX_WAIT_MS = 10000;
          let waited = 0;
          while (!publicVideoId && waited < MAX_WAIT_MS) {
            await new Promise<void>(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
            waited += POLL_INTERVAL_MS;
            const freshEntry = p.videoQueueRef.current.find(v => v.id === p.activeVideoId);
            publicVideoId = freshEntry?.publicVideoId ?? null;
          }
        }

        if (publicVideoId && p.isSignedIn) {
          trackVideoAnalysed({
            prompt,
            prompt_is_custom: !p.prompts.includes(prompt),
            source: 'run_prompt',
            user_plan: p.isPro ? 'pro' : 'free',
            is_authenticated: true,
          });
          // PRI-496 Lane B: public create — decode any compiled-query envelope
          // into an explicit structured query (parse_mode: client).
          const wire = decodeWirePrompt(prompt);
          const res = await createPublicAnalysis({
            video_id: publicVideoId,
            prompt: wire.text,
            query: wire.query,
          });
          if (res.status === 'complete' && res.answer) {
            if (isStale()) return;
            const quickResult: PrimateVisionResult = {
              answer: res.answer,
              confidence: res.confidence ?? null,
              query: prompt,
              query_type: 'object',
              clips: null,
            };
            p.setJobId(res.job_id);
            p.setResult(quickResult);
            p.setDisplayedResult(quickResult);
            p.setDisplayedJobId(res.job_id);
            p.videoKeyRef.current = res.job_id;
            p.setPageStateSync('complete');
            p.resultCacheRef.current.set(prompt, { jobId: res.job_id, result: quickResult });
            const runEnrichGen = generation;
            void getVisionResult(res.job_id).then(full => {
              if (p.analysisGenRef.current === runEnrichGen) {
                p.setResult(full);
                p.setDisplayedResult(full);
              }
              p.resultCacheRef.current.set(prompt, { jobId: res.job_id, result: full });
            }).catch(() => {});
            return;
          } else {
            p.setJobId(res.job_id);
            p.setJobProgress({ stage: 'submitted', pct: 2, message: 'Job submitted to inference...' });
            p.setPageStateSync('processing');
            p.startPolling(res.job_id, prompt, generation);
            return;
          }
        }

        trackVideoUploaded({
          file_size_bytes: p.selectedFile.size,
          file_size_mb: Math.round(p.selectedFile.size / 1024 / 1024 * 100) / 100,
          filename: p.selectedFile.name,
          user_plan: p.isPro ? 'pro' : p.isSignedIn ? 'free' : 'demo',
          is_authenticated: p.isSignedIn,
        });
        trackVideoAnalysed({
          prompt,
          prompt_is_custom: !p.prompts.includes(prompt),
          source: 'fresh',
          user_plan: p.isPro ? 'pro' : p.isSignedIn ? 'free' : 'demo',
          is_authenticated: p.isSignedIn,
        });
        p.setUploadPath('uploading');
        let serverLoadingTimer: ReturnType<typeof setTimeout> | null = null;
        const onUploadProgress = (pct: number) => {
          p.setUploadProgress(pct);
          if (pct >= 100 && !serverLoadingTimer) {
            serverLoadingTimer = setTimeout(() => {
              const currentState = p.pageStateRef.current;
              // guards against failure race condition — don't overwrite failed/cancelled state
              if (currentState === 'uploading' || currentState === 'processing') {
                p.setJobProgress({ stage: 'queued', pct: 1, message: 'Inference server loading — this may take up to 90 seconds on first start...' });
                p.setPageStateSync('processing');
              }
            }, 3000);
          }
        };
        // PRI-496 Lane B: public multipart one-shot (POST /v1/analyses with
        // {video ≤100MB, prompt}) — the exact path an API customer's quick
        // demo uses. Files over the one-shot cap take the presigned
        // create→PUT→complete→create-analysis chain instead.
        const wireFallback = decodeWirePrompt(prompt);
        let job_id: string;
        // One-shot takes prompt-only (no structured-query field) — compiled-
        // query overrides go through the presigned chain so `query` is explicit.
        if (fitsOneShot(p.selectedFile) && !wireFallback.query) {
          const res = await createAnalysisMultipart(p.selectedFile, wireFallback.text, onUploadProgress);
          job_id = res.job_id;
        } else {
          const video = await uploadPublicVideo(p.selectedFile, { onProgress: onUploadProgress });
          const res = await createPublicAnalysis({
            video_id: video.id,
            prompt: wireFallback.text,
            query: wireFallback.query,
          });
          job_id = res.job_id;
        }
        if (serverLoadingTimer) clearTimeout(serverLoadingTimer);
        if (!p.isSignedIn) {
          const key = 'primate-demo-count-' + new Date().toDateString();
          const newCount = p.demoUsageCount + 1;
          localStorage.setItem(key, String(newCount));
          p.setDemoUsageCount(newCount);
        }
        p.setJobId(job_id);
        p.setJobProgress({ stage: 'submitted', pct: 2, message: 'Job submitted to inference...' });
        p.setPageStateSync('processing');
        p.startPolling(job_id, prompt, generation);
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number; code?: string; message?: string };
      if (apiErr?.code === 'demo_quota_exceeded' || apiErr?.code === 'demo_limit_reached') {
        // Only show the demo quota UI for actual demo quota errors
        if (!p.isSignedIn) {
          const key = 'primate-demo-count-' + new Date().toDateString();
          localStorage.setItem(key, String(p.DEMO_LIMIT));
          p.setDemoUsageCount(p.DEMO_LIMIT);
        }
        p.setPageStateSync('quota_exceeded');
      } else if (apiErr?.status === 401 && !p.isSignedIn) {
        // PRI-433 P4: anonymous uploads were removed server-side — route guests
        // to the existing sign-up CTA (same UI as the old demo limit).
        p.setPageStateSync('quota_exceeded');
      } else if (apiErr?.status === 402 || apiErr?.code === 'insufficient_credits') {
        // PRI-433 P4: metered credits exhausted — surface an actionable message.
        p.setAnalysisErrorMessage('You\u2019re out of processing credits. Add credits in Billing to keep analyzing.');
        p.setPageStateSync('failed');
      } else if (apiErr?.status === 429) {
        // Other 429s (rate limit, too_many_jobs) — show as transient error not demo quota
        p.setAnalysisErrorMessage(apiErr?.message ?? 'Too many requests. Please wait a moment and try again.');
        p.setPageStateSync('failed');
      } else if (apiErr?.status === 413 || apiErr?.code === 'file_too_large') {
        p.setFileError('Video is too large. Please use a video under 20 MB.');
        p.setPageStateSync('idle');
        p.setAnalysisErrorMessage(null);
      } else {
        console.error('[PV] analysis error:', apiErr);
        p.setAnalysisErrorMessage(apiErr?.message ?? null);
        p.setPageStateSync('failed');
      }
    }
  }

  async function handleAnalyseWithPrompt(prompt: string, compiledQuery?: CompiledQuery | null): Promise<void> {
    if ((!p.selectedFile && !p.selectedDemoVideo && !p.selectedSavedVideo) || !prompt) return;
    const state = p.pageStateRef.current;
    if (state !== 'idle' && state !== 'complete') return;

    if (p.selectedDemoVideo) {
      const dv = p.demoVideos.find(v => v.id === p.selectedDemoVideo);
      if (dv) {
        const demoResult = dv.results_by_prompt[prompt];
        if (demoResult) {
          p.setSelectedPrompt(prompt);
          p.setRevealPhase('hidden');
          p.setIsDemoResult(true);
          const fakeResult: PrimateVisionResult = {
            answer: demoResult.answer ?? '',
            confidence: demoResult.confidence ?? null,
            query_type: (demoResult as { query_type?: string }).query_type as 'object' | 'action' ?? 'object',
            query: prompt,
            clips: (demoResult as { clips?: Array<{ start_time: number; end_time: number; confidence: number }> | null }).clips ?? null,
          };
          p.setResult(fakeResult);
          p.setDisplayedResult(fakeResult);
          const jobKey = dv.id + '__demo__' + prompt;
          p.setJobId(jobKey);
          p.setDisplayedJobId(jobKey);
          p.videoKeyRef.current = jobKey;
          p.setPageStateSync('complete');
          p.resultCacheRef.current.set(prompt, { jobId: jobKey, result: fakeResult });
          return;
        }
        p.setSelectedPrompt(prompt);
        p.setResult(null);
        p.setDisplayedResult(null);
        p.setDisplayedJobId(null);
        p.setPageStateSync('idle');
        p.setAnalysisErrorMessage(null);
        return;
      }
    }

    if (p.selectedSavedVideo && !p.selectedFile) {
      const cachedSaved = p.resultCacheRef.current.get(prompt);
      if (cachedSaved) {
        p.setSelectedPrompt(prompt);
        p.setResult(cachedSaved.result);
        p.setDisplayedResult(cachedSaved.result);
        p.setJobId(cachedSaved.jobId);
        p.setDisplayedJobId(cachedSaved.jobId);
        p.videoKeyRef.current = cachedSaved.jobId;
        p.setPageStateSync('complete');
        return;
      }
      const storedResult = p.selectedSavedVideo.results_by_prompt?.[prompt];
      if (storedResult) {
        p.setSelectedPrompt(prompt);
        const r: PrimateVisionResult = { answer: storedResult.answer ?? '', confidence: storedResult.confidence ?? null, query_type: 'object', query: prompt, clips: null };
        p.setResult(r);
        p.setDisplayedResult(r);
        const jk = p.selectedSavedVideo.id + '__saved__' + prompt;
        p.setJobId(jk);
        p.setDisplayedJobId(jk);
        p.videoKeyRef.current = jk;
        p.setPageStateSync('complete');
        p.resultCacheRef.current.set(prompt, { jobId: jk, result: r });
        return;
      }
      p.setSelectedPrompt(prompt);
      if (state === 'complete') {
        p.setResult(null);
        p.setDisplayedResult(null);
        p.setDisplayedJobId(null);
        p.videoKeyRef.current = null;
        p.setPageStateSync('idle');
        p.setAnalysisErrorMessage(null);
      }
      const myGen = ++p.analysisGenRef.current;

      // PRI-496 Lane B: saved-library re-runs dispatch through the public
      // plane by the row's public id (video_…). The internal list surfaces
      // public_id; rows created post-cutover store the public id AS id.
      const savedPublicId = p.selectedSavedVideo.public_id
        ?? (p.selectedSavedVideo.id.startsWith('video_') ? p.selectedSavedVideo.id : null);
      // Legacy rows whose source upload never landed (source_s3_key null on
      // the internal list) can't be analyzed — keep the re-upload message.
      // Post-cutover rows (video_ id) always have a server-side source.
      const hasSource = p.selectedSavedVideo.id.startsWith('video_') || p.selectedSavedVideo.source_s3_key != null;
      if (savedPublicId && hasSource && (p.selectedSavedVideo.status == null || p.selectedSavedVideo.status === 'ready')) {
        p.setPageStateSync('uploading');
        try {
          const wire = decodeWirePrompt(prompt);
          const res = await createPublicAnalysis({
            video_id: savedPublicId,
            prompt: wire.text,
            query: (compiledQuery as unknown as Record<string, unknown> | null) ?? wire.query,
          });
          if (myGen !== p.analysisGenRef.current) return;
          if (res.status === 'complete' && res.answer) {
            const quickResult: PrimateVisionResult = { answer: res.answer, confidence: res.confidence ?? null, query_type: 'object', query: prompt, clips: null };
            p.setResult(quickResult);
            p.setDisplayedResult(quickResult);
            p.setJobId(res.job_id);
            p.setDisplayedJobId(res.job_id);
            p.videoKeyRef.current = res.job_id;
            p.setPageStateSync('complete');
            p.resultCacheRef.current.set(prompt, { jobId: res.job_id, result: quickResult });
            void saveUserVideoResult(p.selectedSavedVideo.id, {
              prompt, job_id: res.job_id, answer: res.answer ?? null, confidence: res.confidence ?? null,
            }).catch(() => {});
            void getVisionResult(res.job_id).then(full => {
              if (p.analysisGenRef.current === myGen) { p.setResult(full); p.setDisplayedResult(full); }
              p.resultCacheRef.current.set(prompt, { jobId: res.job_id, result: full });
            }).catch(() => {});
          } else {
            p.setJobId(res.job_id);
            p.setPageStateSync('processing');
            p.startPolling(res.job_id, prompt, myGen);
          }
        } catch { p.setPageStateSync('failed'); }
        return;
      }
      // No public id resolvable (pre-054 legacy row) or video not ready —
      // needs a re-upload.
      p.setResult(null);
      p.setDisplayedResult(null);
      p.setDisplayedJobId(null);
      p.setPageStateSync('failed');
      p.setAnalysisErrorMessage('This video needs to be re-uploaded to run a new analysis. Please delete it from your library and upload it again.');
      return;
    }

    const cached = p.resultCacheRef.current.get(prompt);
    if (cached) {
      p.setSelectedPrompt(prompt);
      p.setResult(cached.result);
      p.setDisplayedResult(cached.result);
      p.setJobId(cached.jobId);
      p.setDisplayedJobId(cached.jobId);
      p.videoKeyRef.current = cached.jobId;
      p.setPageStateSync('complete');
      return;
    }

    if (state === 'complete') {
      p.setResult(null);
      p.setDisplayedResult(null);
      p.setDisplayedJobId(null);
      p.videoKeyRef.current = null;
      p.setPageStateSync('idle');
      p.setAnalysisErrorMessage(null);
    }
    p.setRevealPhase('hidden');
    p.setIsDemoResult(false);
    const myGeneration = ++p.analysisGenRef.current;
    p.setSelectedPrompt(prompt);
    await handleAnalyseInternal(prompt, myGeneration);
  }

  async function handleCancel(): Promise<void> {
    if (p.jobId) { try { await cancelVisionJob(p.jobId); } catch { /* ignore */ } }
    trackAnalysisCancelled(p.isPro ? 'pro' : p.isSignedIn ? 'free' : 'demo');
    p.stopPolling();
    resetToIdle();
  }

  function resetToIdle(): void {
    p.stopPolling();
    p.setPageStateSync('idle');
    p.setAnalysisErrorMessage(null);
    p.setJobId(null);
    p.setResult(null);
    p.setSelectedFile(null);
    p.setUploadProgress(0);
    p.setProcessingStep(0);
    p.setVideoQueue([]);
    p.setActiveVideoId(null);
    p.setResultTab('result');
    p.setUploadPath(null);
    p.setRevealPhase('hidden');
    p.setIsDemoResult(false);
  }

  return {
    handleFileSelected,
    handleConversionConfirm,
    handleConversionCancel,
    handleAnalyseWithPrompt,
    handleCancel,
    resetToIdle,
  };
}
